import { showErrorToast } from "../components/Toastify/toastHelper";
import { auth } from "../firebase/firebaseConfig";
import { FetchAuth } from "../utils/FetchAuthUtil";
import { formatSort } from "../utils/formatSort";

const endpoint = process.env.REACT_APP_API_ENDPOINT;

export class BookService {
    public static async GetBooks(params: {
        page: number;
        limit: number;
        categoryId?: string;
        types?: string;
        grades?: string;
        languages?: string;
        title?: string;
        sort?: string;
        onSale?: boolean;
        publisherId?: string;
        newlyPublished?: boolean | number;
        priceMin?: number;
        priceMax?: number;
    }, signal?: AbortSignal) {
        try {
            const token = await auth.currentUser?.getIdToken() || "";
    
            const queryParams = new URLSearchParams();
    
            // Add parameters only if they are defined
            queryParams.append("page", params.page.toString());
            queryParams.append("limit", params.limit.toString());
            if (params.categoryId) queryParams.append("categoryId", params.categoryId);
            if (params.types) queryParams.append("types", params.types.toString().toLowerCase());
            if (params.grades) queryParams.append("grades", params.grades);
            if (params.languages) queryParams.append("languages", params.languages);
            if (params.title) queryParams.append("title", params.title);
            if (params.publisherId) queryParams.append("publisherId", params.publisherId);
            if (params.onSale !== undefined) queryParams.append("onSale", params.onSale.toString());
            if (params.newlyPublished !== undefined) queryParams.append("newlyPublished", params.newlyPublished.toString());
            if (params.sort) {
                const formattedSort = formatSort(params.sort);
                queryParams.append("sort", formattedSort.toString());
            }
            if (params.priceMin !== undefined) queryParams.append("priceMin", params.priceMin.toString());
            if (params.priceMax !== undefined) queryParams.append("priceMax", params.priceMax.toString());
    
            const response = await fetch(`${endpoint}/book/getBooksWebsite?${queryParams.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                signal
            });
    
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
    
            const data = await response.json();
            return data;
        } catch (er: any) {
            if (er.name === 'AbortError') {
                throw er; // Re-throw AbortError without showing toast
            }
            showErrorToast("An error occurred while fetching books");
            throw new Error(er);
        }
    }


    public static getBooksPublisherId = async ( limit: number, page: number, archived: boolean | null, published: boolean | null, title: string | null, publisherId: string, sortColumn?:string|null,sortDirection?:string): Promise<any> => {
        try {
            const params = new URLSearchParams({
                page: String(page),
                limit: String(limit),
                ...(archived !== null && { archived: String(archived) }),
                ...(published !== null && { published: String(published) }),
                ...(title && { title }),
                publisherId: publisherId
            });

            if (sortColumn && sortDirection) {
                params.append('sort', `${sortColumn}:${sortDirection}`);
            }
            const resp = await FetchAuth(`${endpoint}/book/adminGetBooks?${params.toString()}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });

            return await resp.json();
        } catch (error) {
            throw error;
        }
    }
    
    public static async GetRelatedBooks(bookId: string) {
        try {
            const response = await fetch(`${endpoint}/book/getRelatedBooks/${bookId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
    
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
    
            const data = await response.json();
            return data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }

    public static async GetBooksHome() {
        try {
            const response = await fetch(`${endpoint}/book/getBooksWebsiteHome`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
    
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
    
            const data = await response.json();
            return data;
        } catch (error: any) {
            throw new Error(error.message);
        }
    }
    

    public static async GetTopPurchasedBooks() {
        try {
            const response = await fetch(`${endpoint}/book/getTopPurchasedBooks`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (er: any) {
            throw new Error(er.message);
        }
    }
    public static async GetBookById(id: string) {
        try {
            const token = await auth.currentUser?.getIdToken() || "";
            const response = await fetch(`${endpoint}/book/getWebsiteBookDetails/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (er: any) {
            showErrorToast("An error occurred while fetching book details");
            throw new Error(er);
        }
    }

    public static async ActivateBook(id: string, accessCode: string) {
        try {
            const token = await auth.currentUser?.getIdToken() || "";
            const response = await fetch(`${endpoint}/user/accessBook`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body:JSON.stringify({"accessCode":accessCode,"bookId":id})
                
            });
            const payload = await response.json();

            if (!response.ok) {
                 throw new Error(payload.message || `Error ${response.status}: ${response.statusText}`);
              }
            

            
            return payload;
        } catch (er: any) {
            throw new Error(er.message);
        }
    }

    public static getFile = async (format: "excel" | "csv" | "pdf"): Promise<Blob> => {
        try {
            const resp = await FetchAuth(`${endpoint}/admin/exportBooks?format=${format}`, {
                method: 'GET',
                headers: { "Content-Type": "application/json" }
            });

            return await resp.blob();
        } catch (error) {
            throw error;
        }
    }

    public static getTopFile = async (format: "excel" | "csv", bookId: string, year: string, batchId?: string): Promise<Blob> => {
        try {
            if (batchId) {
                const resp = await FetchAuth(`${endpoint}/admin/exportAnalyticsBatchBook/analytics/book/${bookId}/batch/${batchId}/yearly/export?year=${year}&fileType=${format}`, {
                    method: 'GET',
                    headers: { "Content-Type": "application/json" }
                });



                return await resp.blob();
            } else {
                const resp = await FetchAuth(`${endpoint}/admin/exportAnalyticsOnline/analytics/book/${bookId}/yearly/export?year=${year}&fileType=${format}`, {
                    method: 'GET',
                    headers: { "Content-Type": "application/json" }
                });



                return await resp.blob();

            }

        } catch (error) {
            throw error;
        }
    }

    static async fetchPdf(bookId: string | undefined): Promise<any> {
        const token = await auth.currentUser?.getIdToken() || '';
        const res = await fetch(`${endpoint}/books/${bookId}/link`, {
          method: 'POST',
          headers: {
            'Authorization': `${token}`,
          },
        });

        
        if (!res.ok) {
            throw new Error(`Cannot fetch PDF (status ${res.status})`);
        }
        const payload = await res.json();
        return payload;
      }


    static async downloadRecipet(orderId: string | undefined): Promise<Blob> {
        const token = await auth.currentUser?.getIdToken() || '';
        const res = await fetch(`${endpoint}/order/downloadReciept/${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `${token}`,
          },
        });

        if (!res.ok) {
            throw new Error(`Cannot fetch PDF (status ${res.status})`);
        }
        return await res.blob();
    }


}